var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { UXPContext } from '@core/context/Context';
import Modal from '@widget/popups/Modal';
import Button from '@widget/buttons/Button';
import IconButton from '@widget/buttons/IconButton';
import { Loading } from '@widget/loaders';
import NotificationBlock from '@helper/NotificationBlock';
import { readImageDpi, setImageDpi } from '@widget/forms/inputs/MediaPicker/mediaLibrary';
import { formatFileSize } from '@widget/forms/inputs/FileInput/fileHelper';
const FALLBACK_DPI = 150;
const MAX_ZOOM = 6;
// Space around the frame inside the stage, in CSS pixels
const MARGIN = 24;
const limit = (v) => (v && v > 0 ? v : 0);
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), Math.max(lo, hi));
/**
 * @export
 * Crops a picture to fit size and DPI rules, then hands back the new file. The frame stays fixed
 * on screen; the picture moves (drag, arrow keys) and zooms (wheel, pinch, the slider) under it,
 * and rotates in quarter turns. Nothing is typed in: the result takes the largest size the rules
 * allow (never enlarged) and the rules' highest DPI. One short line over the picture gives the
 * result size, or says to zoom out when the frame holds too few pixels. A picture smaller than the
 * rules' minimum can't be used: it says so and offers "Choose another photo".
 *
 * The frame has the rules' proportions (square for square rules or none; the picture's own shape when
 * only `maxBytes` is set).
 * The result is a JPEG (PNG for a PNG source) with the DPI written into the file.
 *
 * Opens as a dialog (full screen on phones), or renders `inline` inside another component.
 *
 * @example
 * ```tsx
 * <ImageCropper show={!!file} file={file} rules={{ minWidth: 500, maxWidth: 1000, minHeight: 500, maxHeight: 1000, maxDpi: 200 }}
 *     onApply={async (cropped) => { await upload(cropped); setFile(null); }} onCancel={() => setFile(null)} />
 * ```
 *
 * @category Inputs/Pickers/Media
 */
const ImageCropper = (props) => {
    const { file, src, show, inline, rules, initialDpi, roundMask, title, onApply, onCancel, onChooseAnother, className } = props;
    const context = useContext(UXPContext);
    const $L = (key, params) => { var _a, _b; return (_b = (_a = context === null || context === void 0 ? void 0 : context.$L) === null || _a === void 0 ? void 0 : _a.call(context, key, params)) !== null && _b !== void 0 ? _b : key; };
    const active = inline || !!show;
    const minW = limit(rules === null || rules === void 0 ? void 0 : rules.minWidth), maxW = limit(rules === null || rules === void 0 ? void 0 : rules.maxWidth);
    const minH = limit(rules === null || rules === void 0 ? void 0 : rules.minHeight), maxH = limit(rules === null || rules === void 0 ? void 0 : rules.maxHeight);
    const minDpi = limit(rules === null || rules === void 0 ? void 0 : rules.minDpi), maxDpi = limit(rules === null || rules === void 0 ? void 0 : rules.maxDpi);
    const maxBytes = limit(rules === null || rules === void 0 ? void 0 : rules.maxBytes);
    const [round, setRound] = useState(!!roundMask);
    // The picture: its bytes, an object URL for it, and its DPI once loaded
    const [source, setSource] = useState(null);
    const [image, setImage] = useState(null);
    const [originalDpi, setOriginalDpi] = useState(undefined);
    const [loadFailed, setLoadFailed] = useState(false);
    // The view: zoom (1 = the picture just covers the frame), the picture point at the frame's
    // centre (cx, cy, in rotated picture pixels; null = the picture's centre), and the rotation
    const [rotation, setRotation] = useState(0);
    const [view, setView] = useState({ zoom: 1, cx: null, cy: null });
    const [applying, setApplying] = useState(false);
    const [applyError, setApplyError] = useState('');
    // Load the picture whenever the input changes (and the cropper is open)
    useEffect(() => {
        if (!active || (!file && !src))
            return;
        let live = true;
        let url = '';
        setLoadFailed(false);
        setImage(null);
        setSource(null);
        (() => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const blob = file || (yield fetch(src).then(r => { if (!r.ok)
                    throw new Error(r.statusText); return r.blob(); }));
                url = URL.createObjectURL(blob);
                const img = new Image();
                yield new Promise((resolve, reject) => {
                    img.onload = () => resolve();
                    img.onerror = () => reject(new Error('load'));
                    img.src = url;
                });
                const recorded = yield readImageDpi(blob).catch(() => undefined);
                if (!live)
                    return;
                setSource({ blob, url });
                setOriginalDpi(recorded);
                setImage(img);
            }
            catch (_a) {
                if (live)
                    setLoadFailed(true);
            }
        }))();
        return () => {
            live = false;
            if (url)
                URL.revokeObjectURL(url);
        };
    }, [active, file, src]);
    // A new picture or a rotation starts over: whole picture, centred
    useEffect(() => {
        setView({ zoom: 1, cx: null, cy: null });
        setApplyError('');
    }, [image, rotation]);
    useEffect(() => { setRotation(0); }, [image]);
    // ---- The stage and the fixed frame ----
    const stageRef = useRef(null);
    const [area, setArea] = useState({ width: 0, height: 0 });
    useLayoutEffect(() => {
        const el = stageRef.current;
        if (!el)
            return;
        const measure = () => setArea({ width: el.clientWidth, height: el.clientHeight });
        measure();
        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', measure);
            return () => window.removeEventListener('resize', measure);
        }
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [image, loadFailed]);
    // The frame's proportions (width / height): the largest allowed size's, else the smallest's;
    // with only a file size limit, the picture's own shape (cropping is optional); else square
    const turned = rotation % 180 !== 0;
    const pictureAspect = image ? (turned ? image.naturalHeight / image.naturalWidth : image.naturalWidth / image.naturalHeight) : 1;
    const aspect = maxW && maxH ? maxW / maxH : minW && minH ? minW / minH : maxBytes && !(maxW || maxH || minW || minH) ? pictureAspect : 1;
    // Round mask (square frames only): a preview of how the picture shows as an avatar
    const square = aspect === 1;
    const frameW = Math.max(0, Math.min(area.width - 2 * MARGIN, (area.height - 2 * MARGIN) * aspect));
    const frameH = frameW / aspect;
    // The picture as rotated: its width and height in pixels
    const quarter = rotation % 180 !== 0;
    const imgW = image ? (quarter ? image.naturalHeight : image.naturalWidth) : 0;
    const imgH = image ? (quarter ? image.naturalWidth : image.naturalHeight) : 0;
    // Screen pixels per picture pixel at zoom 1: the picture just covers the frame
    const cover = imgW && imgH && frameW ? Math.max(frameW / imgW, frameH / imgH) : 0;
    // Keeps the frame inside the picture: no empty edges
    const settle = (zoom, cx, cy) => {
        const z = clamp(zoom, 1, MAX_ZOOM);
        const s = cover * z;
        const cw = s ? frameW / s : 0, ch = s ? frameH / s : 0;
        return {
            zoom: z,
            cx: clamp(cx !== null && cx !== void 0 ? cx : imgW / 2, cw / 2, imgW - cw / 2),
            cy: clamp(cy !== null && cy !== void 0 ? cy : imgH / 2, ch / 2, imgH - ch / 2),
        };
    };
    const v = settle(view.zoom, view.cx, view.cy);
    const scale = cover * v.zoom;
    const crop = scale ? { w: frameW / scale, h: frameH / scale } : { w: 0, h: 0 };
    // ---- The result: the largest allowed size the frame holds pixels for (never enlarged) ----
    const outSize = (cw) => {
        const w = Math.floor(Math.min(cw, maxW || Infinity, maxH ? maxH * aspect : Infinity));
        return { w: Math.max(1, w), h: Math.max(1, Math.round(w / aspect)) };
    };
    const out = outSize(crop.w);
    const bigEnough = (o) => (!minW || o.w >= minW) && (!minH || o.h >= minH);
    // Even the whole picture holds too few pixels: zooming out can't help
    const photoTooSmall = !!image && !!cover && !bigEnough(outSize(frameW / cover));
    const zoomedTooFar = !photoTooSmall && !bigEnough(out);
    const dpi = maxDpi || Math.round(clamp(initialDpi || originalDpi || FALLBACK_DPI, minDpi || 1, Infinity));
    // ---- Moving and zooming (pointer events: mouse, pen, touch; two fingers pinch) ----
    const pointers = useRef(new Map());
    const pinch = useRef(0);
    const [dragging, setDragging] = useState(false);
    const onPointerDown = (e) => {
        var _a, _b;
        if (!scale || photoTooSmall || e.button > 0)
            return;
        (_b = (_a = e.currentTarget).setPointerCapture) === null || _b === void 0 ? void 0 : _b.call(_a, e.pointerId);
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        pinch.current = 0;
        setDragging(true);
    };
    const onPointerMove = (e) => {
        const map = pointers.current;
        const last = map.get(e.pointerId);
        if (!last)
            return;
        map.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (map.size === 1) {
            const dx = e.clientX - last.x, dy = e.clientY - last.y;
            setView(p => settle(p.zoom, v.cx - dx / scale, v.cy - dy / scale));
            return;
        }
        const [a, b] = Array.from(map.values());
        const dist = Math.hypot(a.x - b.x, a.y - b.y);
        if (pinch.current) {
            const ratio = dist / pinch.current;
            setView(() => settle(v.zoom * ratio, v.cx, v.cy));
        }
        pinch.current = dist;
    };
    const onPointerUp = (e) => {
        pointers.current.delete(e.pointerId);
        pinch.current = 0;
        if (!pointers.current.size)
            setDragging(false);
    };
    const zoomBy = (factor) => setView(() => settle(v.zoom * factor, v.cx, v.cy));
    // The wheel zooms; it needs a non-passive listener to keep the page from scrolling
    const wheel = useRef();
    wheel.current = (e) => {
        if (!scale || photoTooSmall)
            return;
        e.preventDefault();
        zoomBy(Math.exp(-e.deltaY * 0.0015));
    };
    useEffect(() => {
        const el = stageRef.current;
        if (!el)
            return;
        const handler = (e) => { var _a; return (_a = wheel.current) === null || _a === void 0 ? void 0 : _a.call(wheel, e); };
        el.addEventListener('wheel', handler, { passive: false });
        return () => el.removeEventListener('wheel', handler);
    }, [image, loadFailed]);
    // Arrow keys move the picture (10 screen pixels, 50 with Shift); + and - zoom
    const onKeyDown = (e) => {
        if (!scale || photoTooSmall)
            return;
        const step = (e.shiftKey ? 50 : 10) / scale;
        const moves = {
            ArrowLeft: [step, 0], ArrowRight: [-step, 0], ArrowUp: [0, step], ArrowDown: [0, -step],
        };
        if (moves[e.key]) {
            e.preventDefault();
            setView(() => settle(v.zoom, v.cx + moves[e.key][0], v.cy + moves[e.key][1]));
        }
        else if (e.key === '+' || e.key === '=')
            zoomBy(1.1);
        else if (e.key === '-')
            zoomBy(1 / 1.1);
    };
    // ---- Apply: draw the frame's area at the result size, then write the DPI into the file ----
    const apply = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!image || !source || !scale || photoTooSmall || zoomedTooFar)
            return;
        setApplying(true);
        setApplyError('');
        try {
            // A round mask saves a round picture: PNG, transparent outside the circle
            const circle = round && square;
            const png = circle || source.blob.type === 'image/png';
            const type = png ? 'image/png' : 'image/jpeg';
            const canvas = document.createElement('canvas');
            canvas.width = out.w;
            canvas.height = out.h;
            const ctx = canvas.getContext('2d');
            if (!ctx)
                throw new Error('canvas');
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            if (circle) {
                ctx.beginPath();
                ctx.arc(out.w / 2, out.h / 2, out.w / 2, 0, Math.PI * 2);
                ctx.clip();
            }
            if (!png) {
                // JPEG has no transparency: transparent areas become white, not black
                ctx.fillStyle = '#fff';
                ctx.fillRect(0, 0, out.w, out.h);
            }
            ctx.scale(out.w / crop.w, out.h / crop.h);
            ctx.translate(-(v.cx - crop.w / 2), -(v.cy - crop.h / 2));
            ctx.translate(imgW / 2, imgH / 2);
            ctx.rotate(rotation * Math.PI / 180);
            ctx.drawImage(image, -image.naturalWidth / 2, -image.naturalHeight / 2);
            const encode = (c, t, q) => new Promise(resolve => c.toBlob(resolve, t, q));
            let blob = yield encode(canvas, type, png ? undefined : 0.92);
            if (!blob)
                throw new Error('encode');
            let outType = type;
            // A file size limit: lower the JPEG quality, then the size, until it fits (a round
            // picture stays PNG; any other PNG over the limit is saved as JPEG on white)
            if (maxBytes && blob.size > maxBytes && !circle) {
                let current = canvas;
                if (png) {
                    const white = document.createElement('canvas');
                    white.width = canvas.width;
                    white.height = canvas.height;
                    const wctx = white.getContext('2d');
                    if (!wctx)
                        throw new Error('canvas');
                    wctx.fillStyle = '#fff';
                    wctx.fillRect(0, 0, white.width, white.height);
                    wctx.drawImage(canvas, 0, 0);
                    current = white;
                    outType = 'image/jpeg';
                }
                const minWidth = Math.max(minW || 1, minH ? minH * aspect : 1);
                search: for (let pass = 0; pass < 12; pass++) {
                    for (const q of [0.92, 0.85, 0.75, 0.65, 0.55]) {
                        const next = yield encode(current, 'image/jpeg', q);
                        if (next && next.size <= maxBytes) {
                            blob = next;
                            break search;
                        }
                    }
                    const w = Math.floor(current.width * 0.85);
                    if (w < minWidth)
                        break;
                    const smaller = document.createElement('canvas');
                    smaller.width = w;
                    smaller.height = Math.max(1, Math.round(w * current.height / current.width));
                    const sctx = smaller.getContext('2d');
                    if (!sctx)
                        throw new Error('canvas');
                    sctx.imageSmoothingEnabled = true;
                    sctx.imageSmoothingQuality = 'high';
                    sctx.drawImage(current, 0, 0, smaller.width, smaller.height);
                    current = smaller;
                }
                if (blob.size > maxBytes) {
                    setApplyError($L('uxp-core.image-cropper.too-large'));
                    return;
                }
            }
            const jpeg = outType === 'image/jpeg';
            const withDpi = yield setImageDpi(blob, dpi);
            const original = (file === null || file === void 0 ? void 0 : file.name) || (src ? src.split(/[?#]/)[0].split('/').pop() : '') || 'image';
            const base = original.replace(/\.[^.]+$/, '') || 'image';
            yield onApply(new File([withDpi], `${base}.${jpeg ? 'jpg' : 'png'}`, { type: outType }));
        }
        catch (_a) {
            setApplyError($L('uxp-core.image-cropper.apply-failed'));
        }
        finally {
            setApplying(false);
        }
    });
    const close = () => { if (!applying)
        onCancel(); };
    // ---- Render ----
    // The rotated picture's centre sits (imgW/2 - cx, imgH/2 - cy) picture pixels from the frame's centre
    const imageStyle = image && scale ? {
        width: image.naturalWidth * scale,
        height: image.naturalHeight * scale,
        left: area.width / 2 + (imgW / 2 - v.cx) * scale - image.naturalWidth * scale / 2,
        top: area.height / 2 + (imgH / 2 - v.cy) * scale - image.naturalHeight * scale / 2,
        transform: `rotate(${rotation}deg)`,
    } : undefined;
    // Above the picture: success when the result fits the rules, warning when it doesn't,
    // danger when loading or saving failed
    const notice = applyError || loadFailed
        ? { variant: 'danger', message: applyError || $L('uxp-core.image-cropper.load-failed') }
        : photoTooSmall ? { variant: 'warning', message: $L('uxp-core.image-cropper.photo-too-small') }
            : zoomedTooFar ? { variant: 'warning', message: $L('uxp-core.image-cropper.zoom-out') }
                : image && scale ? { variant: 'success', message: $L('uxp-core.image-cropper.result-size', { width: out.w, height: out.h }) + (maxBytes ? ` · ${$L('uxp-core.image-cropper.up-to', { size: formatFileSize(maxBytes) })}` : '') }
                    : null;
    const stage = _jsxs("div", { ref: stageRef, className: classNames('uxpcore_imagecropper__stage', { 'uxpcore_imagecropper__stage--dragging': dragging }), tabIndex: 0, role: "group", "aria-label": $L('uxp-core.image-cropper.frame'), onPointerDown: onPointerDown, onPointerMove: onPointerMove, onPointerUp: onPointerUp, onPointerCancel: onPointerUp, onKeyDown: onKeyDown, children: [imageStyle && _jsx("img", { className: "uxpcore_imagecropper__image", src: source === null || source === void 0 ? void 0 : source.url, alt: "", draggable: false, style: imageStyle }), imageStyle && _jsxs("div", { className: classNames('uxpcore_imagecropper__frame', { 'uxpcore_imagecropper__frame--round': round && square }), style: { width: frameW, height: frameH }, children: [_jsx("span", { className: "uxpcore_imagecropper__thirds uxpcore_imagecropper__thirds--v" }), _jsx("span", { className: "uxpcore_imagecropper__thirds uxpcore_imagecropper__thirds--h" })] }), !image && !loadFailed && _jsx(Loading, {})] });
    const zoomRow = _jsxs("div", { className: "uxpcore_imagecropper__zoomrow", children: [_jsx(IconButton, { icon: "fal minus", tooltip: $L('uxp-core.image-cropper.zoom-out-button'), disabled: !scale || photoTooSmall, onClick: () => zoomBy(1 / 1.25) }), _jsx("input", { type: "range", className: "uxpcore_imagecropper__zoom", min: 1, max: MAX_ZOOM, step: 0.01, value: v.zoom, "aria-label": $L('uxp-core.image-cropper.zoom'), disabled: !scale || photoTooSmall, onChange: e => setView(() => settle(Number(e.target.value), v.cx, v.cy)) }), _jsx(IconButton, { icon: "fal plus", tooltip: $L('uxp-core.image-cropper.zoom-in'), disabled: !scale || photoTooSmall, onClick: () => zoomBy(1.25) }), _jsx("span", { className: "uxpcore_imagecropper__divider" }), _jsx(IconButton, { icon: "fal rotate-right", tooltip: $L('uxp-core.image-cropper.rotate'), disabled: !image || photoTooSmall, onClick: () => setRotation(r => (r + 90) % 360) }), square && _jsx(IconButton, { icon: round ? 'fal square' : 'fal circle', tooltip: $L(round ? 'uxp-core.image-cropper.square' : 'uxp-core.image-cropper.round'), disabled: !image || photoTooSmall, onClick: () => setRound(r => !r) })] });
    const footer = _jsxs("div", { className: "uxpcore_imagecropper__footer", children: [_jsx(Button, { title: $L('uxp-core.text.cancel'), variant: "secondary", disabled: applying, onClick: close }), _jsx(Button, { title: $L('uxp-core.image-cropper.apply'), variant: "primary", loading: applying, disabled: applying || !scale || photoTooSmall || zoomedTooFar, onClick: apply })] });
    const body = _jsxs("div", { className: classNames('uxpcore_imagecropper', { 'uxpcore_imagecropper--inline': inline }, className), children: [notice && _jsx(NotificationBlock, { class: "uxpcore_imagecropper__notice", variant: notice.variant, mode: "compact", layout: "bordered", icon: true, message: notice.message, action: photoTooSmall && onChooseAnother && _jsx(Button, { title: $L('uxp-core.image-cropper.choose-another'), variant: "secondary", size: "small", onClick: onChooseAnother }) }), stage, zoomRow, footer] });
    if (inline)
        return body;
    return _jsx(Modal, { show: !!show, onClose: close, title: title || $L('uxp-core.image-cropper.title'), width: "44rem", backgroundDismiss: false, children: show && body });
};
export default ImageCropper;
